import { useCallback, useEffect, useState } from 'react';
import type { DatoveSchrankyPrilohaDto } from '@gov-nx/api';
import { useDownloadEvents, useMessageEvents } from '@gov-nx/core/events';
import { LocalizeNameSpaceTypes, useLocale } from '@gov-nx/core/service';
import type { Nullable } from '@gov-nx/core/types';
import type { DataBoxFolderType } from '@gov-nx/module/data-box';
import { useAttachmentDownload } from '@gov-nx/module/data-box';
import type { DownloadCallback } from '../Download/types';

export const DownloadAttachmentListener = () => {
	const { t } = useLocale(LocalizeNameSpaceTypes.Service);
	const [attachmentData, setAttachmentData] = useState<null | {
		dataBoxId: string;
		messageId: number;
		folderType: DataBoxFolderType;
		attachment: DatoveSchrankyPrilohaDto;
	}>(null);
	const { toastMessageSuccess, toastMessageError } = useMessageEvents();
	const { download } = useDownloadEvents();
	const [callback, setCallback] = useState<{
		[key: string]: Nullable<DownloadCallback<string>['callback']>;
	}>({});

	useDownloadEvents({
		onDataBoxAttachmentDownload: (_, { attachmentData, callback }) => {
			setAttachmentData(attachmentData);
			if (callback) {
				setCallback({
					...callback,
					[attachmentData.attachment.hashPrilohy as string]: callback,
				});
			}
		},
	});

	const onDownload = useCallback(
		(id: string, success: Nullable<boolean>, error: Nullable<Error>) => {
			if (typeof id !== 'string') {
				return;
			}
			const callbackFunction = callback[id];
			if (callbackFunction) {
				callbackFunction(id, success, error);
				setCallback({ ...callback, [id]: null });
			}
			setAttachmentData(null);
		},
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
		[callback, attachmentData]
	);

	const { mutate } = useAttachmentDownload({
		onSuccess: (data) => {
			download({
				...data,
				id: data.id.toString(),
				callback: (id, success, error) => {
					if (success) {
						successMessage(data.fileName);
						onDownload(data.id, true, null);
					}
					if (error) {
						errorMessage();
						onDownload(data.id, null, error);
					}
				},
			});
		},
		onError: (id, error) => {
			errorMessage();
			onDownload(id, null, error);
		},
	});

	const successMessage = (fileName: string) => {
		toastMessageSuccess(
			t('soubor.stazeni-souboru-uspech', {
				fileName,
			})
		);
	};

	const errorMessage = () => {
		toastMessageError(t('soubor.stazeni-souboru-chyba'));
	};

	useEffect(() => {
		if (attachmentData) {
			mutate(attachmentData);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps -- These dependencies are correct
	}, [attachmentData]);

	return null;
};
